// src/pages/monitoring/MonitoringDashboard.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, Clock, CheckCircle, TrendingUp,
  Download, Bell, Shield,
  Eye, ChevronRight, ChevronLeft, Hourglass,
  AlertCircle, PlayCircle, Users, ArrowUpRight,
  Calendar, Activity, HelpCircle, Info, XCircle, Search
} from 'lucide-react';
import { Button, Badge, Card, Breadcrumb, ProgressBar, Select, Loading, Modal } from '../../components/ui';
import {
  ResponsiveContainer, Tooltip,
  PieChart, Pie, Cell
} from 'recharts';
import {
  monitoringService,
  DashboardKPI,
  OverdueItem,
  ReminderData,
  RiwayatPengingatItem,
  UnitPelaporPerformance,
  UnitPicPerformance,
  UnitPerformancePerspective,
} from '../../services/monitoring';
import { PpeppRekomendasi, Periode, TingkatRisiko } from '../../types';
import { useCapabilities } from '../../hooks/useCapabilities';
import { useRole } from '../../hooks/useRole';
import { useAuthStore } from '../../store/authStore';
import { getStatusLabel, STATUS_CONFIG } from '../../utils/constants';
import toast from 'react-hot-toast';

// ========== CONSTANTS ==========
const PPEPP_PER_PAGE = 5;
const MAX_UNITS_DISPLAY = 8; // Maksimal unit yang ditampilkan di chart
const MODAL_PAGE_SIZE = 8;
const STATUS_DASHBOARD_ORDER = [
  'diajukan',
  'validasi_l1',
  'validasi_l2',
  'antrean_rapim',
  'diputuskan',
  'berlangsung',
  'menunggu_konfirmasi',
  'selesai',
  'diarsipkan',
  'diselesaikan_lokal',
  'ditolak_l1',
  'ditolak_l2',
] as const;

const MONITORING_STATUS_COLORS: Record<string, string> = {
  diajukan: '#3B82F6',
  validasi_l1: '#06B6D4',
  validasi_l2: '#6366F1',
  antrean_rapim: '#8B5CF6',
  diputuskan: '#A855F7',
  berlangsung: '#F59E0B',
  menunggu_konfirmasi: '#F97316',
  selesai: '#16A34A',
  diarsipkan: '#64748B',
  diselesaikan_lokal: '#0EA5E9',
  ditolak_l1: '#EF4444',
  ditolak_l2: '#B91C1C',
};

// Tab configuration untuk PPEPP
const PPEPP_TABS = [
  { id: 'semua', label: 'Semua', icon: null, color: 'text-neutral-text-primary' },
  { id: 'berlangsung', label: 'Aktif', icon: PlayCircle, color: 'text-warning' },
  { id: 'menunggu_konfirmasi', label: 'Menunggu Konfirmasi', icon: Hourglass, color: 'text-amber-600' },
  { id: 'terhambat', label: 'Terhambat', icon: AlertCircle, color: 'text-danger' },
  { id: 'selesai_final', label: 'Selesai', icon: CheckCircle, color: 'text-success' },
] as const;

type PpeppTabId = typeof PPEPP_TABS[number]['id'];

type ModalMeta = {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
};

const DEFAULT_MODAL_META: ModalMeta = {
  current_page: 1,
  per_page: MODAL_PAGE_SIZE,
  total: 0,
  last_page: 1,
};

const DEFAULT_PPEPP_SUMMARY = {
  semua: 0,
  terhambat: 0,
  berlangsung: 0,
  menunggu_konfirmasi: 0,
  selesai: 0,
  arsip: 0,
};

const UNIT_PERSPECTIVE_META: Record<UnitPerformancePerspective, {
  title: string;
  modalTitle: string;
  empty: string;
}> = {
  pelapor: {
    title: 'Aktivitas Pelaporan per Unit',
    modalTitle: 'Semua Aktivitas Pelaporan per Unit',
    empty: 'Belum ada aktivitas pelaporan unit pada periode ini.',
  },
  pic: {
    title: 'Kinerja Tindak Lanjut per Unit PIC',
    modalTitle: 'Semua Kinerja Tindak Lanjut per Unit PIC',
    empty: 'Belum ada tindak lanjut yang ditugaskan ke unit PIC pada periode ini.',
  },
};

// ========== CUSTOM COMPONENTS ==========

// KPI Card dengan gradient dan animasi
interface KpiCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconGradient?: string;
  label: string;
  value: number | string;
  valueColor?: string;
  subtitle?: string;
  pulse?: boolean;
  trend?: 'up' | 'down' | 'neutral';
}

const KpiCard: React.FC<KpiCardProps> = ({
  icon,
  iconBg,
  iconGradient,
  label,
  value,
  valueColor = 'text-neutral-text-primary',
  subtitle,
  pulse = false,
  trend }) => (
  <div className="relative group bg-white rounded-2xl border border-neutral-border/50 p-5 shadow-sm hover:shadow-lg hover:border-neutral-border transition-all duration-300 overflow-hidden">
    {/* Subtle gradient background on hover */}
    <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${iconGradient || 'bg-gradient-to-br from-teal-50/50 to-transparent'}`} />

    {/* Pulse indicator */}
    {pulse && (
      <div className="absolute top-3 right-3">
        <span className="flex h-2.5 w-2.5 relative">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-danger"></span>
        </span>
      </div>
    )}

    <div className="relative z-10 flex items-start gap-4">
      <div className={`p-3 rounded-xl ${iconBg} shadow-sm`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary mb-1">
          {label}
        </p>
        <p className={`text-3xl font-black tracking-tight ${valueColor}`}>
          {value}
        </p>
        {subtitle && (
          <p className="text-[11px] text-neutral-text-secondary mt-0.5 flex items-center gap-1">
            {trend === 'up' && <ArrowUpRight className="w-3 h-3 text-success" />}
            {trend === 'down' && <ArrowUpRight className="w-3 h-3 text-danger rotate-90" />}
            {subtitle}
          </p>
        )}
      </div>
    </div>
  </div>
);

// Section Header Component
interface SectionHeaderProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, title, subtitle, action }) => (
  <div className="flex items-start justify-between mb-4">
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-xl bg-gradient-to-br from-teal-main/10 to-teal-main/5">
        {icon}
      </div>
      <div>
        <h3 className="text-base font-bold text-teal-dark">{title}</h3>
        {subtitle && <p className="text-xs text-neutral-text-secondary">{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

const InfoTooltipButton: React.FC<{
  title: string;
  content: string;
  align?: 'left' | 'right';
}> = ({ title, content, align = 'right' }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
        aria-label={title}
        aria-expanded={open}
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Tutup penjelasan"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div
            className={`absolute top-full mt-2 z-20 w-80 max-w-[min(20rem,calc(100vw-2rem))] rounded-2xl border border-blue-100 bg-white p-3 shadow-xl ${align === 'left' ? 'left-0' : 'right-0'
              }`}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-blue-700">{title}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-neutral-text-secondary">{content}</p>
          </div>
        </>
      )}
    </div>
  );
};

// ========== MAIN COMPONENT ==========
const MonitoringDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const roleInfo = useRole();
  const user = useAuthStore((s) => s.user);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState<DashboardKPI | null>(null);
  const [overdueList, setOverdueList] = useState<OverdueItem[]>([]);
  const [ppeppList, setPpeppList] = useState<PpeppRekomendasi[]>([]);
  const [unitPelaporPerformance, setUnitPelaporPerformance] = useState<UnitPelaporPerformance[]>([]);
  const [unitPicPerformance, setUnitPicPerformance] = useState<UnitPicPerformance[]>([]);
  const [unitPerspective, setUnitPerspective] = useState<UnitPerformancePerspective>('pic');
  const [reminders, setReminders] = useState<ReminderData[]>([]);
  const [reminderHistory, setReminderHistory] = useState<RiwayatPengingatItem[]>([]);
  const [periodeList, setPeriodeList] = useState<Periode[]>([]);
  const [isPeriodeReady, setIsPeriodeReady] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  // PPEPP Tab & Pagination State
  const [ppeppActiveTab, setPpeppActiveTab] = useState<PpeppTabId>('semua');
  const [ppeppCurrentPage, setPpeppCurrentPage] = useState(1);

  // Dedicated modals for Monitoring details
  const [showPpeppModal, setShowPpeppModal] = useState(false);
  const [showOverdueModal, setShowOverdueModal] = useState(false);

  const [ppeppModalLoading, setPpeppModalLoading] = useState(false);
  const [ppeppModalPage, setPpeppModalPage] = useState(1);
  const [ppeppModalTab, setPpeppModalTab] = useState<PpeppTabId>('semua');
  const [ppeppModalSearchDraft, setPpeppModalSearchDraft] = useState('');
  const [ppeppModalSearch, setPpeppModalSearch] = useState('');
  const [ppeppModalData, setPpeppModalData] = useState<PpeppRekomendasi[]>([]);
  const [ppeppModalMeta, setPpeppModalMeta] = useState<ModalMeta>(DEFAULT_MODAL_META);
  const [ppeppModalSummary, setPpeppModalSummary] = useState(DEFAULT_PPEPP_SUMMARY);

  const [overdueModalLoading, setOverdueModalLoading] = useState(false);
  const [overdueModalPage, setOverdueModalPage] = useState(1);
  const [overdueModalSearchDraft, setOverdueModalSearchDraft] = useState('');
  const [overdueModalSearch, setOverdueModalSearch] = useState('');
  const [overdueModalData, setOverdueModalData] = useState<OverdueItem[]>([]);
  const [overdueModalMeta, setOverdueModalMeta] = useState<ModalMeta>(DEFAULT_MODAL_META);

  // Unit Performance - Modal & Search
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [searchUnitQuery, setSearchUnitQuery] = useState('');

  const { canManageRapim } = useCapabilities();

  useEffect(() => {
    const requestedTab = searchParams.get('ppepp_tab');
    const shouldOpenPpepp = searchParams.get('open') === 'ppepp';
    const shouldOpenOverdue = searchParams.get('open') === 'overdue';
    const validTabs = new Set(PPEPP_TABS.map((tab) => tab.id));

    if (!requestedTab && !shouldOpenPpepp && !shouldOpenOverdue) return;

    if (requestedTab && validTabs.has(requestedTab as PpeppTabId)) {
      const nextTab = requestedTab as PpeppTabId;
      setPpeppActiveTab(nextTab);
      setPpeppModalTab(nextTab);
      setPpeppCurrentPage(1);
      setPpeppModalPage(1);
    }

    if (shouldOpenPpepp) {
      setPpeppModalSearch('');
      setPpeppModalSearchDraft('');
      setShowPpeppModal(true);
    }

    if (shouldOpenOverdue) {
      setOverdueModalSearch('');
      setOverdueModalSearchDraft('');
      setOverdueModalPage(1);
      setShowOverdueModal(true);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('ppepp_tab');
    nextParams.delete('open');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // ========== ACCESS CONTROL ==========
  // Guard sudah dihandle oleh CapabilityRoute di App.tsx

  // ========== FETCH DATA ==========
  useEffect(() => {
    if (!isPeriodeReady) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const filters = periodFilter !== 'all' ? { periode_id: Number(periodFilter) } : undefined;

        const results = await Promise.allSettled([
          monitoringService.getDashboard(filters),
          monitoringService.getOverdue(filters ? { ...filters, per_page: 5 } : { per_page: 5 }),
          monitoringService.getProgressPpepp(filters ? { ...filters, per_page: 100 } : { per_page: 100 }),
          monitoringService.getUnitPerformance({ ...filters, perspective: 'pelapor' }),
          monitoringService.getUnitPerformance({ ...filters, perspective: 'pic' }),
          monitoringService.getReminders(),
          canManageRapim
            ? monitoringService.getReminderHistory({ per_page: 5 })
            : Promise.resolve({ data: [] }),
        ]);

        const [kpiRes, overdueRes, ppeppRes, pelaporUnitRes, picUnitRes, reminderRes, reminderHistoryRes] = results;

        const isNetworkError = (reason: any) => !reason?.response;
        const coreFailures = [kpiRes, overdueRes, ppeppRes, pelaporUnitRes, picUnitRes].filter(r => r.status === 'rejected');
        if (coreFailures.length === 5 && coreFailures.every(r => isNetworkError((r as PromiseRejectedResult).reason))) {
          toast.error('Backend tidak dapat diakses. Pastikan server berjalan dan URL API sudah benar.');
        } else if (results.some(r => r.status === 'rejected')) {
          toast.error('Sebagian data monitoring gagal dimuat.');
        }

        if (kpiRes.status === 'fulfilled') {
          setKpi((kpiRes.value as any)?.data || kpiRes.value);
        }

        if (overdueRes.status === 'fulfilled') {
          const overdueData = (overdueRes.value as any)?.data || overdueRes.value;
          setOverdueList(Array.isArray(overdueData) ? overdueData : (overdueData?.data || []));
        } else {
          setOverdueList([]);
        }

        if (ppeppRes.status === 'fulfilled') {
          const ppeppData = (ppeppRes.value as any)?.data || ppeppRes.value;
          setPpeppList(Array.isArray(ppeppData) ? ppeppData : (ppeppData?.data || []));
        } else {
          setPpeppList([]);
        }

        if (pelaporUnitRes.status === 'fulfilled') {
          const unitData = (pelaporUnitRes.value as any)?.data || pelaporUnitRes.value;
          setUnitPelaporPerformance(Array.isArray(unitData) ? unitData as UnitPelaporPerformance[] : (unitData?.data || []));
        } else {
          setUnitPelaporPerformance([]);
        }

        if (picUnitRes.status === 'fulfilled') {
          const unitData = (picUnitRes.value as any)?.data || picUnitRes.value;
          setUnitPicPerformance(Array.isArray(unitData) ? unitData as UnitPicPerformance[] : (unitData?.data || []));
        } else {
          setUnitPicPerformance([]);
        }

        if (reminderRes.status === 'fulfilled') {
          const remData = (reminderRes.value as any)?.data || reminderRes.value;
          setReminders(Array.isArray(remData) ? remData : []);
        } else {
          setReminders([]);
        }

        if (reminderHistoryRes.status === 'fulfilled') {
          const historyData = (reminderHistoryRes.value as any)?.data || reminderHistoryRes.value;
          setReminderHistory(Array.isArray(historyData) ? historyData : (historyData?.data || []));
        } else {
          setReminderHistory([]);
        }
      } catch (e) {
        console.error(e);
        toast.error('Gagal memuat data monitoring');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [periodFilter, canManageRapim, isPeriodeReady]);

  useEffect(() => {
    const fetchPeriode = async () => {
      try {
        const data = await monitoringService.getPeriodeList();
        const normalizedPeriode = Array.isArray(data) ? data : [];
        setPeriodeList(normalizedPeriode);

        const activePeriode = normalizedPeriode.find((p) => p.is_active);
        setPeriodFilter(activePeriode ? String(activePeriode.id) : 'all');
      } catch (e) {
        console.error('Gagal memuat daftar periode:', e);
        toast.error('Gagal memuat daftar periode');
      } finally {
        setIsPeriodeReady(true);
      }
    };
    fetchPeriode();
  }, []);

  // Reset pagination when tab changes
  useEffect(() => {
    setPpeppCurrentPage(1);
  }, [ppeppActiveTab]);

  useEffect(() => {
    if (!showPpeppModal) return;

    let cancelled = false;

    const fetchPpeppModal = async () => {
      setPpeppModalLoading(true);
      try {
        const response = await monitoringService.getProgressPpepp({
          ...(periodFilter !== 'all' ? { periode_id: Number(periodFilter) } : {}),
          status: ppeppModalTab === 'semua' ? undefined : ppeppModalTab,
          search: ppeppModalSearch || undefined,
          page: ppeppModalPage,
          per_page: MODAL_PAGE_SIZE,
        });

        if (cancelled) return;

        setPpeppModalData(Array.isArray(response.data) ? response.data : []);
        setPpeppModalMeta(response.meta || DEFAULT_MODAL_META);
        setPpeppModalSummary(response.summary || DEFAULT_PPEPP_SUMMARY);
      } catch (error) {
        if (!cancelled) {
          toast.error('Gagal memuat daftar tindak lanjut');
          setPpeppModalData([]);
          setPpeppModalMeta(DEFAULT_MODAL_META);
          setPpeppModalSummary(DEFAULT_PPEPP_SUMMARY);
        }
      } finally {
        if (!cancelled) {
          setPpeppModalLoading(false);
        }
      }
    };

    fetchPpeppModal();

    return () => {
      cancelled = true;
    };
  }, [showPpeppModal, ppeppModalPage, ppeppModalSearch, ppeppModalTab, periodFilter]);

  useEffect(() => {
    if (!showOverdueModal) return;

    let cancelled = false;

    const fetchOverdueModal = async () => {
      setOverdueModalLoading(true);
      try {
        const response = await monitoringService.getOverdue({
          ...(periodFilter !== 'all' ? { periode_id: Number(periodFilter) } : {}),
          search: overdueModalSearch || undefined,
          page: overdueModalPage,
          per_page: MODAL_PAGE_SIZE,
        });

        if (cancelled) return;

        setOverdueModalData(Array.isArray(response.data) ? response.data : []);
        setOverdueModalMeta(response.meta || {
          ...DEFAULT_MODAL_META,
          total: Array.isArray(response.data) ? response.data.length : 0,
          per_page: Array.isArray(response.data) ? response.data.length : DEFAULT_MODAL_META.per_page,
        });
      } catch (error) {
        if (!cancelled) {
          toast.error('Gagal memuat daftar laporan terlambat');
          setOverdueModalData([]);
          setOverdueModalMeta(DEFAULT_MODAL_META);
        }
      } finally {
        if (!cancelled) {
          setOverdueModalLoading(false);
        }
      }
    };

    fetchOverdueModal();

    return () => {
      cancelled = true;
    };
  }, [showOverdueModal, overdueModalPage, overdueModalSearch, periodFilter]);

  // ========== HANDLERS ==========
  const handleExport = async () => {
    try {
      const arrayBuffer = await monitoringService.exportReport('pdf', periodFilter !== 'all' ? { periode_id: Number(periodFilter) } : undefined);
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `monitoring-report-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export Report Error:', e);
      toast.error('Gagal mengexport laporan');
    }
  };

  const handleSendReminder = async (laporanId: number) => {
    try {
      const result = await monitoringService.sendReminder(laporanId);
      toast.success((result as any)?.message || 'Pengingat berhasil dikirim');

      if (canManageRapim) {
        const historyRes = await monitoringService.getReminderHistory({ per_page: 5 }).catch(() => ({ data: [] }));
        const historyData = (historyRes as any)?.data || historyRes;
        setReminderHistory(Array.isArray(historyData) ? historyData : (historyData?.data || []));
      }
    } catch (e) {
      toast.error('Gagal mengirim pengingat');
    }
  };

  const openPpeppModal = () => {
    setPpeppModalTab(ppeppActiveTab);
    setPpeppModalPage(1);
    setPpeppModalSearchDraft('');
    setPpeppModalSearch('');
    setShowPpeppModal(true);
  };

  const openOverdueModal = () => {
    setOverdueModalPage(1);
    setOverdueModalSearchDraft('');
    setOverdueModalSearch('');
    setShowOverdueModal(true);
  };

  const normalizeOverdueDays = (value: unknown): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.round(Math.abs(parsed)));
  };

  const toRiskLevel = (risk: string | undefined): TingkatRisiko => {
    const validRisks: TingkatRisiko[] = ['rendah', 'sedang', 'tinggi', 'kritis'];
    const normalized = (risk || 'sedang').toLowerCase() as TingkatRisiko;
    return validRisks.includes(normalized) ? normalized : 'sedang';
  };

  const getPpeppDisplayState = (item: any, isOverdue: boolean) => {
    const kondisi = String(item?.kondisi_selesai || '').toLowerCase();
    const progress = Number(item?.progress_percentage || 0);
    const progressCount = Number(item?.progress_count || 0);
    const hasNoProgressYet = kondisi === 'berlangsung' && progress <= 0 && progressCount === 0;

    if (kondisi === 'selesai_final') {
      return {
        label: 'Selesai',
        badgeClass: 'bg-success/10 text-success',
        progressColor: 'success' as const,
      };
    }

    if (kondisi === 'terhambat') {
      return {
        label: 'Terhambat',
        badgeClass: 'bg-danger/10 text-danger',
        progressColor: 'danger' as const,
      };
    }

    if (kondisi === 'menunggu_konfirmasi') {
      return {
        label: 'Menunggu Konfirmasi',
        badgeClass: 'bg-amber-100 text-amber-700',
        progressColor: 'warning' as const,
      };
    }

    if (hasNoProgressYet) {
      return {
        label: 'Belum Ada Progress',
        badgeClass: isOverdue ? 'bg-danger/10 text-danger' : 'bg-amber-100 text-amber-700',
        progressColor: isOverdue ? ('danger' as const) : ('warning' as const),
      };
    }

    return {
      label: 'Tindak Lanjut Aktif',
      badgeClass: isOverdue ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning',
      progressColor: isOverdue ? ('danger' as const) : ('teal' as const),
    };
  };

  // ========== PPEPP FILTERING & PAGINATION ==========
  const ppeppStatusCounts = useMemo(() => {
    const counts: Record<PpeppTabId, number> = {
      semua: ppeppList.length,
      berlangsung: 0,
      menunggu_konfirmasi: 0,
      terhambat: 0,
      selesai_final: 0
    };

    ppeppList.forEach((item) => {
      const kondisi = (item as any).kondisi_selesai?.toLowerCase() || '';
      if (kondisi === 'berlangsung') counts.berlangsung++;
      else if (kondisi === 'menunggu_konfirmasi') counts.menunggu_konfirmasi++;
      else if (kondisi === 'terhambat') counts.terhambat++;
      else if (kondisi === 'selesai_final') counts.selesai_final++;
    });

    return counts;
  }, [ppeppList]);

  const filteredPpeppList = useMemo(() => {
    if (ppeppActiveTab === 'semua') return ppeppList;
    return ppeppList.filter((item) => {
      const kondisi = (item as any).kondisi_selesai?.toLowerCase() || '';
      return kondisi === ppeppActiveTab;
    });
  }, [ppeppList, ppeppActiveTab]);

  const ppeppTotalPages = Math.ceil(filteredPpeppList.length / PPEPP_PER_PAGE);
  const paginatedPpeppList = useMemo(() => {
    const startIndex = (ppeppCurrentPage - 1) * PPEPP_PER_PAGE;
    return filteredPpeppList.slice(startIndex, startIndex + PPEPP_PER_PAGE);
  }, [filteredPpeppList, ppeppCurrentPage]);

  // ========== COMPUTED VALUES ==========
  const statusPieData = Object.entries(kpi?.laporan_per_status || {})
    .filter(([key]) => !['tugas_ppepp_aktif', 'draf'].includes(key))
    .map(([key, value]) => ({
      key,
      name: key === 'berlangsung' ? 'Tindak Lanjut Aktif' : getStatusLabel(key),
      value,
      color: MONITORING_STATUS_COLORS[key] || STATUS_CONFIG[key]?.color || '#9CA3AF',
      order: STATUS_DASHBOARD_ORDER.indexOf(key as typeof STATUS_DASHBOARD_ORDER[number]),
    }))
    .filter(d => d.value > 0)
    .sort((a, b) => {
      const orderA = a.order === -1 ? 999 : a.order;
      const orderB = b.order === -1 ? 999 : b.order;
      return orderA - orderB;
    });

  const inProgressStatuses = ['draf', 'diajukan', 'validasi_l1', 'validasi_l2', 'antrean_rapim', 'diputuskan', 'berlangsung'];
  const inProgressCount = Object.entries(kpi?.laporan_per_status || {})
    .filter(([k]) => inProgressStatuses.includes(k))
    .reduce((acc, [, v]) => acc + v, 0);

  const totalSelesaiFinal =
    (kpi?.laporan_per_status?.['selesai'] || 0) +
    ((kpi?.laporan_per_status as any)?.['diarsipkan'] || 0) +
    ((kpi?.laporan_per_status as any)?.['diselesaikan_lokal'] || 0);

  const periodeOptions = [
    { value: 'all', label: 'Semua Periode' },
    ...periodeList.map((periode) => ({
      value: String(periode.id),
      label: `${periode.nama}${periode.is_active ? ' (Aktif)' : ''}`
    })),
  ];

  const selectedPeriodeLabel = periodeOptions.find((option) => option.value === periodFilter)?.label || 'Semua Periode';

  const ppeppModalCounts: Record<PpeppTabId, number> = {
    semua: ppeppModalSummary.semua,
    berlangsung: ppeppModalSummary.berlangsung,
    menunggu_konfirmasi: ppeppModalSummary.menunggu_konfirmasi,
    terhambat: ppeppModalSummary.terhambat,
    selesai_final: ppeppModalSummary.selesai,
  };

  const activeUnitPerformance = useMemo(
    () => (unitPerspective === 'pic' ? unitPicPerformance : unitPelaporPerformance),
    [unitPerspective, unitPicPerformance, unitPelaporPerformance]
  );

  const sortedActiveUnitPerformance = useMemo(() => {
    return [...activeUnitPerformance].sort((a, b) => {
      const rateDiff = (b.completion_rate || 0) - (a.completion_rate || 0);
      if (rateDiff !== 0) return rateDiff;
      return (b.total || 0) - (a.total || 0);
    });
  }, [activeUnitPerformance]);

  const filteredModalUnits = useMemo(() => {
    if (!searchUnitQuery) return sortedActiveUnitPerformance;
    const query = searchUnitQuery.toLowerCase();
    return sortedActiveUnitPerformance.filter((u) =>
      u.unit && u.unit.toLowerCase().includes(query)
    );
  }, [sortedActiveUnitPerformance, searchUnitQuery]);

  const displayedUnits = sortedActiveUnitPerformance.slice(0, MAX_UNITS_DISPLAY);
  const userUnitName = user?.unit?.name || 'unit Anda';
  const unitCopy = useMemo(() => {
    const meta = UNIT_PERSPECTIVE_META[unitPerspective];

    const audienceContext = roleInfo.isRektor || roleInfo.isAdmin || roleInfo.isObserver || canManageRapim
      ? {
        pelaporSubtitle: 'Melihat unit asal dari laporan yang masuk pada area yang Anda pantau.',
        pelaporHelper:
          'Data ini fokus pada unit asal laporan. Jika sebuah laporan kemudian ditindaklanjuti oleh PIC dari unit lain, laporan tetap tercatat pada unit asalnya agar sumber laporan tetap jelas.',
        picSubtitle: 'Melihat unit yang menerima tugas tindak lanjut sebagai PIC.',
        picHelper:
          'Data ini fokus pada unit pelaksana tindak lanjut. Jadi unit yang tampil bisa berbeda dari unit asal laporan, karena PIC dapat ditunjuk dari unit lain yang dianggap paling sesuai mengerjakan tindak lanjut.',
      }
      : roleInfo.isWarek
        ? {
          pelaporSubtitle: 'Melihat unit asal laporan pada bidang yang Anda pantau.',
          pelaporHelper:
            'Data ini membantu melihat dari unit mana laporan pada bidang Anda berasal. Jika tindak lanjutnya dikerjakan unit lain yang masih terkait, laporan tetap tercatat pada unit asalnya.',
          picSubtitle: 'Melihat unit pelaksana tindak lanjut pada bidang yang Anda pantau.',
          picHelper:
            'Data ini membantu melihat unit mana yang benar-benar mengerjakan tindak lanjut pada bidang Anda. Karena PIC bisa ditunjuk dari unit lain, nama unit yang tampil dapat berbeda dari unit asal laporannya.',
        }
        : {
          pelaporSubtitle: `Melihat laporan yang berasal dari ${userUnitName} atau lingkup yang Anda pimpin.`,
          pelaporHelper:
            `Data ini tetap menghitung unit asal laporan. Jadi jika laporan dari ${userUnitName} ditindaklanjuti oleh PIC dari unit lain, laporan tetap tercatat pada unit asalnya.`,
          picSubtitle: `Melihat unit yang mengerjakan tindak lanjut pada laporan di ${userUnitName} atau lingkup Anda.`,
          picHelper:
            `Data ini mengikuti unit yang ditunjuk sebagai PIC. Karena itu, unit lain bisa tampil di sini jika laporan dari ${userUnitName} dikerjakan oleh PIC dari unit tersebut.`,
        };

    return {
      ...meta,
      subtitle: unitPerspective === 'pelapor' ? audienceContext.pelaporSubtitle : audienceContext.picSubtitle,
      helper: unitPerspective === 'pelapor' ? audienceContext.pelaporHelper : audienceContext.picHelper,
      helperTitle: unitPerspective === 'pelapor' ? 'Cara membaca data pelaporan' : 'Cara membaca data tindak lanjut',
    };
  }, [unitPerspective, roleInfo, canManageRapim, userUnitName]);
  const getRateBarClass = (rate: number) => (rate >= 80 ? 'bg-success' : rate >= 50 ? 'bg-warning' : 'bg-danger');
  const getRateBgClass = (rate: number) => (rate >= 80 ? 'bg-success/10' : rate >= 50 ? 'bg-warning/10' : 'bg-danger/10');
  const getRateTextClass = (rate: number) => (rate >= 80 ? 'text-success' : rate >= 50 ? 'text-warning' : 'text-danger');
  const unitColumnLabels = unitPerspective === 'pic'
    ? { first: 'Selesai', second: 'Hambatan', third: 'Avg %' }
    : { first: 'Selesai', second: 'Overdue', third: 'Rate' };

  // ========== LOADING STATE ==========
  if (loading) {
    return <Loading variant="page" message="Memuat data..." />;
  }

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Monitoring' },
      ]} />

      {/* Header - Clean Style without background */}
      <div className="relative mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-teal-main" />
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-main">Real-time</span>
            </div>
            <h1 className="text-2xl font-black tracking-tight text-teal-dark">Dashboard Monitoring</h1>
            <p className="text-sm text-neutral-text-secondary mt-1">Pantau progress dan tindak lanjut secara real-time</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGuide((prev) => !prev)}
              icon={<HelpCircle className="w-4 h-4" />}
            >
              Panduan
            </Button>
            <Select
              options={periodeOptions}
              value={periodFilter}
              onChange={setPeriodFilter}
              className="min-w-[180px]"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              icon={<Download className="w-4 h-4" />}
            >
              Export
            </Button>
          </div>
        </div>
      </div>

      {showGuide && (
        <Card className="border border-teal-main/10 bg-gradient-to-r from-white via-white to-teal-main/5 animate-in">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-teal-main/10 shrink-0">
                <Info className="w-5 h-5 text-teal-main" />
              </div>
              <div>
                <h2 className="text-base font-bold text-teal-dark">Cara membaca dashboard Monitoring</h2>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Panduan singkat agar user memahami prioritas angka dan blok monitoring yang tampil.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowGuide(false)}
              className="text-neutral-text-secondary hover:text-teal-main transition-colors shrink-0"
              aria-label="Tutup panduan dashboard Monitoring"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Keterlambatan</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menunjukkan laporan yang melewati target waktu dan perlu perhatian lebih dulu.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Masih Berjalan</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Berisi laporan yang masih berjalan di jalur validasi, RAPIM, atau tindak lanjut PIC.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Selesai</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Angka ini mencakup laporan yang telah tuntas melalui Rapat Pimpinan dan seluruh tindak lanjut PIC-nya sudah selesai final, laporan yang diarsipkan, serta laporan yang diselesaikan langsung pada level pimpinan unit yang berwenang seperti Dekan, Kepala Lembaga, atau Warek sesuai kewenangannya.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Progress Penyelesaian</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Dihitung dengan rumus
                {' '}
                <span className="font-semibold text-neutral-text-primary">
                  (selesai + diarsipkan + selesai langsung) / total laporan x 100%
                </span>
                . Persentase ini menggambarkan tingkat ketuntasan akhir seluruh laporan, baik yang selesai melalui RAPIM maupun yang ditangani langsung di level pimpinan unit.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Progress Tindak Lanjut (PPEPP)</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Blok PPEPP memantau tahap pelaksanaan tindak lanjut setelah sebuah laporan diputuskan. Fokusnya adalah progres kerja PIC, status pelaksanaan, hambatan, dan konfirmasi penyelesaian sampai tindak lanjut benar-benar tuntas.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* KPI Summary - Bento Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<AlertTriangle className="w-6 h-6 text-danger" />}
          iconBg="bg-danger/10"
          iconGradient="bg-gradient-to-br from-red-50 to-transparent"
          label="Keterlambatan"
          value={kpi?.laporan_overdue || 0}
          valueColor="text-danger"
          subtitle="Perlu penanganan segera"
          pulse={(kpi?.laporan_overdue || 0) > 0}
        />
        <KpiCard
          icon={<Clock className="w-6 h-6 text-warning" />}
          iconBg="bg-warning/10"
          iconGradient="bg-gradient-to-br from-amber-50 to-transparent"
          label="Tindak Lanjut Aktif"
          value={inProgressCount}
          valueColor="text-warning"
          subtitle={`${ppeppStatusCounts.berlangsung} tindak lanjut aktif`}
        />
        <KpiCard
          icon={<CheckCircle className="w-6 h-6 text-success" />}
          iconBg="bg-success/10"
          iconGradient="bg-gradient-to-br from-emerald-50 to-transparent"
          label="Selesai"
          value={totalSelesaiFinal}
          valueColor="text-success"
          subtitle={`Termasuk. ${kpi?.laporan_per_status?.['diselesaikan_lokal'] || 0} Diselesaikan Internal`}
          trend="up"
        />
        <KpiCard
          icon={<TrendingUp className="w-6 h-6 text-teal-main" />}
          iconBg="bg-teal-main/10"
          iconGradient="bg-gradient-to-br from-teal-50 to-transparent"
          label="Progress Penyelesaian"
          value={`${Math.round(kpi?.progress_ppepp || 0)}%`}
          valueColor="text-teal-main"
          subtitle="RAPIM + Diselesaikan Internal"
        />
      </div>

      {/* ===== BARIS 2: Pengingat + Overdue (PALING PENTING) ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Pengingat Batas Waktu — DIPINDAHKAN KE ATAS */}
        <Card padding="lg" className="border-2 border-amber-200 bg-gradient-to-br from-amber-50/50 to-white">
          <SectionHeader
            icon={<Bell className="w-5 h-5 text-warning" />}
            title="Pengingat Batas Waktu"
            subtitle="Laporan yang mendekati tenggat"
          />
          {reminders.length > 0 ? (
            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-warning via-orange-400 to-danger" />
              <div className="space-y-3">
                {reminders.slice(0, 5).map((r, i) => {
                  const isOverdue = r.tipe === 'overdue';
                  const isUrgent = r.tipe === 'h-1';
                  const isWarning = r.tipe === 'h-3';

                  const bgColor = isOverdue
                    ? 'from-danger/10 to-transparent border-danger/20'
                    : isUrgent
                      ? 'from-red-50 to-transparent border-red-200'
                      : isWarning
                        ? 'from-orange-50 to-transparent border-orange-200'
                        : 'from-yellow-50 to-transparent border-yellow-200';

                  const dotColor = isOverdue ? 'bg-danger' : isUrgent ? 'bg-red-500' : isWarning ? 'bg-orange-500' : 'bg-yellow-500';

                  return (
                    <Link
                      key={i}
                      to={`/laporan/${r.laporan_id}`}
                      className={`group relative flex items-center gap-4 pl-10 pr-4 py-3 rounded-xl bg-gradient-to-r ${bgColor} border hover:shadow-md transition-all`}
                    >
                      <div className={`absolute left-3.5 w-3 h-3 rounded-full ${dotColor} ring-4 ring-white`} />

                      <div className={`shrink-0 text-center ${isOverdue ? 'text-danger' : isUrgent ? 'text-red-600' : isWarning ? 'text-orange-600' : 'text-yellow-600'}`}>
                        <p className="text-xl font-black leading-none">
                          {isOverdue ? `-${Math.abs(Math.round(r.sisa_hari))}` : Math.round(r.sisa_hari)}
                        </p>
                        <p className="text-[9px] font-bold uppercase">
                          {isOverdue ? 'Telat' : 'Hari'}
                        </p>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono text-teal-main">{r.kode}</p>
                        <p className="text-sm font-semibold text-neutral-text-primary truncate">{r.judul}</p>
                        <p className="text-xs text-neutral-text-secondary">
                          PIC: {r.pic} • {r.target_efektif ? new Date(r.target_efektif).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '-'}
                        </p>
                      </div>

                      {canManageRapim && !isOverdue && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleSendReminder(r.laporan_id);
                          }}
                          className="p-2 rounded-lg bg-warning/10 hover:bg-warning/20 text-warning opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Bell className="w-4 h-4" />
                        </button>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mb-3">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
              <p className="text-sm font-semibold text-success">Semua deadline aman!</p>
              <p className="text-xs text-neutral-text-secondary mt-1">Tidak ada pengingat mendekati tenggat</p>
            </div>
          )}
        </Card>

        {/* Overdue Table */}
        <Card padding="lg">
          <SectionHeader
            icon={<AlertTriangle className="w-5 h-5 text-danger" />}
            title="Laporan Melewati Batas Waktu"
            subtitle={`${overdueList.length} laporan perlu segera ditindaklanjuti`}
            action={
              <button
                onClick={openOverdueModal}
                className="text-xs font-semibold text-teal-main hover:text-teal-dark flex items-center gap-1 transition-colors"
              >
                Lihat Semua <ChevronRight className="w-3 h-3" />
              </button>
            }
          />
          <div className="space-y-2">
            {overdueList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mb-3">
                  <CheckCircle className="w-6 h-6 text-success" />
                </div>
                <p className="text-sm font-semibold text-success">Tidak ada keterlambatan!</p>
                <p className="text-xs text-neutral-text-secondary mt-1">Semua dalam target waktu</p>
              </div>
            ) : (
              overdueList.map((item, i) => {
                const days = normalizeOverdueDays(item.hari_terlambat);
                return (
                  <Link
                    key={i}
                    to={`/laporan/${item.laporan?.id}`}
                    className="group flex items-center gap-4 p-3 rounded-xl bg-gradient-to-r from-danger/5 to-transparent border border-danger/10 hover:border-danger/30 hover:shadow-md transition-all"
                  >
                    <div className="shrink-0 w-12 text-center">
                      <div className="inline-flex flex-col items-center justify-center w-10 h-10 rounded-xl bg-danger/10 group-hover:bg-danger/20 transition-colors">
                        <p className="text-lg font-black text-danger leading-none">{days}</p>
                        <p className="text-[8px] font-bold text-danger uppercase">Hari</p>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">{item.laporan?.kode}</span>
                        <Badge variant="risk" risk={toRiskLevel(item.laporan?.tingkat_risiko)} />
                      </div>
                      <p className="text-sm font-semibold text-neutral-text-primary truncate group-hover:text-danger transition-colors">{item.laporan?.judul}</p>
                      <p className="text-xs text-neutral-text-secondary">{item.laporan?.unit?.name} • PIC: {item.pic}</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-neutral-text-secondary opacity-0 group-hover:opacity-100 shrink-0" />
                  </Link>
                );
              })
            )}
          </div>
        </Card>
      </div>

      {/* ===== BARIS 3: Status Chart + Kinerja Unit ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution - Donut Chart */}
        <Card padding="lg">
          <SectionHeader
            icon={<Shield className="w-5 h-5 text-teal-main" />}
            title="Sebaran Status Laporan"
            subtitle={`${statusPieData.reduce((a, b) => a + b.value, 0)} total laporan`}
          />

          {statusPieData.length > 0 ? (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    stroke="none"
                  >
                    {statusPieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => `${v} laporan`}
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Legend */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-2 w-full mt-2">
                {statusPieData.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 p-1.5 rounded-lg bg-neutral-bg/50">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="text-[10px] text-neutral-text-secondary truncate flex-1">{item.name}</span>
                    <span className="text-xs font-bold">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[260px] flex items-center justify-center">
              <p className="text-sm text-neutral-text-secondary">Belum ada data status.</p>
            </div>
          )}
        </Card>

        {/* Perspektif Unit */}
        <Card padding="lg">
          <SectionHeader
            icon={<Users className="w-5 h-5 text-teal-main" />}
            title={
              <span className="inline-flex items-center gap-2">
                <span>{unitCopy.title}</span>
                <InfoTooltipButton
                  title={unitCopy.helperTitle}
                  content={unitCopy.helper}
                />
              </span>
            }
            subtitle={`${sortedActiveUnitPerformance.length} unit terpantau • ${unitCopy.subtitle}`}
            action={
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 p-1 rounded-lg bg-neutral-bg/70">
                  <button
                    type="button"
                    onClick={() => setUnitPerspective('pic')}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${unitPerspective === 'pic' ? 'bg-white text-teal-main shadow-sm' : 'text-neutral-text-secondary hover:text-neutral-text-primary'}`}
                  >
                    Tindak Lanjut
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnitPerspective('pelapor')}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${unitPerspective === 'pelapor' ? 'bg-white text-teal-main shadow-sm' : 'text-neutral-text-secondary hover:text-neutral-text-primary'}`}
                  >
                    Pelaporan
                  </button>
                </div>
                {sortedActiveUnitPerformance.length > MAX_UNITS_DISPLAY && (
                  <button
                    onClick={() => setShowUnitModal(true)}
                    className="text-xs font-semibold text-teal-main hover:text-teal-dark transition-colors"
                  >
                    Lihat Semua ({sortedActiveUnitPerformance.length})
                  </button>
                )}
              </div>
            }
          />

          {sortedActiveUnitPerformance.length > 0 ? (
            <div className="space-y-2">
              {displayedUnits.length > 3 && (
                <div className="hidden sm:grid sm:grid-cols-12 gap-2 px-3 py-1.5 text-[9px] font-bold text-neutral-text-secondary uppercase tracking-wider">
                  <div className="col-span-1">No</div>
                  <div className="col-span-4">Unit</div>
                  <div className="col-span-4">Rasio Tuntas</div>
                  <div className="col-span-1 text-center">{unitColumnLabels.first}</div>
                  <div className="col-span-1 text-center">{unitColumnLabels.second}</div>
                  <div className="col-span-1 text-center">{unitColumnLabels.third}</div>
                </div>
              )}

              {displayedUnits.map((unit, i) => {
                const rate = unit.completion_rate || 0;
                const colorClass = getRateBarClass(rate);
                const bgClass = getRateBgClass(rate);
                const textClass = getRateTextClass(rate);
                const secondaryCount = unitPerspective === 'pic'
                  ? (unit as UnitPicPerformance).terhambat
                  : (unit as UnitPelaporPerformance).overdue;
                const thirdValue = unitPerspective === 'pic'
                  ? `${(unit as UnitPicPerformance).avg_progress}%`
                  : `${rate}%`;

                return (
                  <div key={i} className={`group p-3 rounded-xl border border-neutral-border/50 hover:border-neutral-border hover:shadow-sm transition-all ${bgClass}`}>
                    {/* Layout mobile */}
                    <div className="sm:hidden">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs font-bold text-neutral-text-secondary w-5">{i + 1}</span>
                          <span className="text-sm font-semibold text-neutral-text-primary truncate">{unit.unit}</span>
                        </div>
                        <span className={`text-sm font-black ${textClass}`}>
                          {rate}%
                        </span>
                      </div>
                      <div className="w-full bg-neutral-border/30 rounded-full h-2 overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${colorClass}`} style={{ width: `${rate}%` }} />
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-neutral-text-secondary">
                        <span className="text-success font-semibold">{unit.selesai} selesai</span>
                        {secondaryCount > 0 && (
                          <span className="text-danger font-semibold">
                            {secondaryCount} {unitPerspective === 'pic' ? 'hambatan' : 'overdue'}
                          </span>
                        )}
                        {unitPerspective === 'pic' && (
                          <span className="text-info font-semibold">{(unit as UnitPicPerformance).avg_progress}% rata-rata</span>
                        )}
                        <span>Total: {unit.total}</span>
                      </div>
                    </div>

                    {/* Layout desktop — grid row */}
                    <div className="hidden sm:grid sm:grid-cols-12 gap-2 items-center">
                      <div className="col-span-1">
                        <span className="text-xs font-bold text-neutral-text-secondary">{i + 1}</span>
                      </div>
                      <div className="col-span-4">
                        <span className="text-sm font-semibold text-neutral-text-primary truncate block" title={unit.unit}>{unit.unit}</span>
                      </div>
                      <div className="col-span-4">
                        <div className="w-full bg-neutral-border/30 rounded-full h-2 overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-500 ${colorClass}`} style={{ width: `${rate}%` }} />
                        </div>
                      </div>
                      <div className="col-span-1 text-center">
                        <span className="text-xs font-bold text-success">{unit.selesai}</span>
                      </div>
                      <div className="col-span-1 text-center">
                        <span className={`text-xs font-bold ${secondaryCount > 0 ? 'text-danger' : 'text-neutral-text-secondary'}`}>
                          {secondaryCount}
                        </span>
                      </div>
                      <div className="col-span-1 text-center">
                        <span className={`text-sm font-black ${unitPerspective === 'pic' ? 'text-info' : textClass}`}>
                          {thirdValue}
                        </span>
                      </div>
                    </div>

                    {secondaryCount > 0 && (
                      <div className="mt-2 flex items-center gap-1 text-[10px] text-danger sm:hidden">
                        <AlertTriangle className="w-3 h-3" />
                        <span>
                          {secondaryCount} {unitPerspective === 'pic' ? 'tindak lanjut terhambat' : 'laporan melewati batas waktu'}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <Users className="w-8 h-8 text-neutral-text-secondary opacity-30 mb-2" />
              <p className="text-sm text-neutral-text-secondary">{unitCopy.empty}</p>
            </div>
          )}
        </Card>
      </div>

      {/* PPEPP Progress Tracking - Full Width Premium Card */}
      <Card padding="lg" className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-teal-main/5 to-transparent rounded-full -mr-32 -mt-32" />

        <div className="relative z-10">
          <SectionHeader
            icon={<TrendingUp className="w-5 h-5 text-teal-main" />}
            title="Progress Tindak Lanjut (PPEPP)"
            subtitle="Pantau tahap pelaksanaan tindak lanjut oleh PIC setelah keputusan ditetapkan"
            action={
              <button
                onClick={openPpeppModal}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-main/10 hover:bg-teal-main/20 text-teal-main text-xs font-semibold rounded-lg transition-colors"
              >
                Lihat Semua <ChevronRight className="w-3 h-3" />
              </button>
            }
          />

          {/* Tab Navigation - Premium Style */}
          <div className="flex items-center gap-1 p-1 bg-neutral-bg/50 rounded-xl mb-6 overflow-x-auto">
            {PPEPP_TABS.map((tab) => {
              const count = ppeppStatusCounts[tab.id];
              const isActive = ppeppActiveTab === tab.id;
              const IconComponent = tab.icon;

              return (
                <button
                  key={tab.id}
                  onClick={() => setPpeppActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap
                    ${isActive
                      ? 'bg-white text-teal-main shadow-sm'
                      : 'text-neutral-text-secondary hover:text-neutral-text-primary hover:bg-white/50'
                    }
                  `}
                >
                  {IconComponent && <IconComponent className="w-4 h-4" />}
                  <span>{tab.label}</span>
                  <span className={`
                    min-w-[22px] h-[22px] flex items-center justify-center rounded-full text-[11px] font-bold
                    ${isActive
                      ? 'bg-teal-main/10 text-teal-main'
                      : count > 0
                        ? 'bg-neutral-border/50 text-neutral-text-primary'
                        : 'bg-neutral-border/30 text-neutral-text-secondary'
                    }
                  `}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* PPEPP List */}
          <div className="space-y-3">
            {paginatedPpeppList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-full bg-neutral-bg flex items-center justify-center mb-4">
                  <TrendingUp className="w-8 h-8 text-neutral-text-secondary opacity-40" />
                </div>
                <p className="text-sm font-semibold text-neutral-text-primary">
                  {ppeppActiveTab === 'semua'
                    ? 'Belum ada tindak lanjut.'
                    : `Tidak ada tindak lanjut dengan status "${PPEPP_TABS.find(t => t.id === ppeppActiveTab)?.label}".`
                  }
                </p>
              </div>
            ) : (
              paginatedPpeppList.map((item) => {
                const daysRemaining = item.target_date
                  ? Math.ceil((new Date(item.target_date).getTime() - new Date().setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24))
                  : null;
                const isOverdue = daysRemaining !== null && daysRemaining < 0;
                const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 7;
                const kondisi = (item as any).kondisi_selesai;
                const isSelesai = kondisi === 'selesai_final';
                const isTerhambat = kondisi === 'terhambat';
                const isMenunggu = kondisi === 'menunggu_konfirmasi';
                const displayState = getPpeppDisplayState(item, isOverdue);
                const progressTextClass = displayState.progressColor === 'success'
                  ? 'text-success'
                  : displayState.progressColor === 'danger'
                    ? 'text-danger'
                    : displayState.progressColor === 'warning'
                      ? 'text-amber-700'
                      : 'text-teal-main';

                const cardBg = isSelesai
                  ? 'bg-gradient-to-r from-success/5 to-transparent border-success/20 hover:border-success/40'
                  : isTerhambat
                    ? 'bg-gradient-to-r from-danger/5 to-transparent border-danger/20 hover:border-danger/40'
                    : isMenunggu
                      ? 'bg-gradient-to-r from-amber-50 to-transparent border-amber-200 hover:border-amber-300'
                      : isOverdue
                        ? 'bg-gradient-to-r from-danger/5 to-transparent border-danger/20 hover:border-danger/40'
                        : isUrgent
                          ? 'bg-gradient-to-r from-warning/5 to-transparent border-warning/20 hover:border-warning/40'
                          : 'bg-white border-neutral-border hover:border-teal-main/30';

                return (
                  <div
                    key={item.id}
                    className={`group p-5 rounded-2xl border transition-all duration-300 hover:shadow-lg ${cardBg}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">
                            {(item as any).laporan_masalah?.kode || (item as any).laporanMasalah?.kode}
                          </span>

                          {/* Status Badge */}
                          <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${displayState.badgeClass}`}>
                            {displayState.label}
                          </span>

                          {/* Days Indicator */}
                          {daysRemaining !== null && !isSelesai && (
                            <span className={`text-[11px] font-semibold ${isOverdue ? 'text-danger' : isUrgent ? 'text-warning' : 'text-neutral-text-secondary'
                              }`}>
                              {isOverdue ? `${Math.abs(daysRemaining)}hr terlambat` : `${daysRemaining}hr lagi`}
                            </span>
                          )}
                        </div>

                        <h4 className="text-base font-bold text-neutral-text-primary truncate mb-1">
                          {(item as any).laporan_masalah?.judul || (item as any).laporanMasalah?.judul}
                          {(item as any).laporan_masalah?.status === 'diselesaikan_lokal' && (
                            <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-md font-bold uppercase">
                              LOKAL
                            </span>
                          )}
                        </h4>

                        <div className="flex items-center gap-4 text-xs text-neutral-text-secondary">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {item.pic_user?.name || item.pic_unit?.name || '-'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {item.target_date ? new Date(item.target_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '-'}
                          </span>
                        </div>
                      </div>

                      <Link to={`/tindak-lanjut/${item.id}`}>
                        <button className="p-2.5 rounded-xl bg-neutral-bg group-hover:bg-teal-main/10 text-neutral-text-secondary group-hover:text-teal-main transition-colors">
                          <Eye className="w-5 h-5" />
                        </button>
                      </Link>
                    </div>

                    {/* Progress Bar */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <ProgressBar
                          value={item.progress_percentage || 0}
                          color={displayState.progressColor}
                          size="sm"
                          showLabel={false}
                        />
                      </div>
                      <span className={`text-sm font-black min-w-[45px] text-right ${progressTextClass}`}>
                        {item.progress_percentage || 0}%
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination - Premium Style */}
          {ppeppTotalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-neutral-border">
              <p className="text-xs text-neutral-text-secondary">
                Menampilkan <span className="font-semibold text-neutral-text-primary">
                  {((ppeppCurrentPage - 1) * PPEPP_PER_PAGE) + 1} - {Math.min(ppeppCurrentPage * PPEPP_PER_PAGE, filteredPpeppList.length)}
                </span> dari <span className="font-semibold text-neutral-text-primary">{filteredPpeppList.length}</span> tindak lanjut
              </p>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPpeppCurrentPage(p => Math.max(1, p - 1))}
                  disabled={ppeppCurrentPage === 1}
                  className={`p-2 rounded-lg transition-colors ${ppeppCurrentPage === 1
                    ? 'text-neutral-text-secondary/30 cursor-not-allowed'
                    : 'text-neutral-text-secondary hover:bg-neutral-bg hover:text-teal-main'
                    }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-1 px-2">
                  {Array.from({ length: ppeppTotalPages }, (_, i) => i + 1)
                    .filter(page => {
                      if (page === 1 || page === ppeppTotalPages) return true;
                      if (Math.abs(page - ppeppCurrentPage) <= 1) return true;
                      return false;
                    })
                    .map((page, index, arr) => {
                      const showEllipsisBefore = index > 0 && page - arr[index - 1] > 1;

                      return (
                        <React.Fragment key={page}>
                          {showEllipsisBefore && (
                            <span className="px-2 text-neutral-text-secondary">•••</span>
                          )}
                          <button
                            onClick={() => setPpeppCurrentPage(page)}
                            className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-semibold transition-all ${ppeppCurrentPage === page
                              ? 'bg-teal-main text-white shadow-sm'
                              : 'text-neutral-text-secondary hover:bg-neutral-bg hover:text-neutral-text-primary'
                              }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                </div>

                <button
                  onClick={() => setPpeppCurrentPage(p => Math.min(ppeppTotalPages, p + 1))}
                  disabled={ppeppCurrentPage === ppeppTotalPages}
                  className={`p-2 rounded-lg transition-colors ${ppeppCurrentPage === ppeppTotalPages
                    ? 'text-neutral-text-secondary/30 cursor-not-allowed'
                    : 'text-neutral-text-secondary hover:bg-neutral-bg hover:text-teal-main'
                    }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Riwayat Pengingat - Compact Table Style */}
      {canManageRapim && (
        <Card padding="lg">
          <SectionHeader
            icon={<Bell className="w-5 h-5 text-info" />}
            title="Riwayat Pengiriman Pengingat"
            subtitle="5 pengiriman terakhir"
            action={
              reminderHistory.length > 0 && (
                <button
                  onClick={async () => {
                    if (!window.confirm('Bersihkan seluruh riwayat pengingat?')) return;
                    try {
                      await monitoringService.clearReminderHistory('all');
                      toast.success('Riwayat pengingat berhasil dibersihkan');
                      setReminderHistory([]);
                    } catch (e) {
                      toast.error('Gagal membersihkan riwayat pengingat');
                    }
                  }}
                  className="text-xs font-semibold text-danger hover:underline"
                >
                  Bersihkan Riwayat
                </button>
              )
            }
          />

          {reminderHistory.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-neutral-border">
              <table className="w-full">
                <thead>
                  <tr className="bg-neutral-bg/50">
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">Waktu</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">Laporan</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">Penerima</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">Kanal</th>
                    <th className="px-4 py-3 text-center text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-border">
                  {reminderHistory.slice(0, 5).map((log) => (
                    <tr key={log.id} className="hover:bg-neutral-bg/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-xs text-neutral-text-secondary">
                          {log.dikirim_pada ? new Date(log.dikirim_pada).toLocaleString('id-ID', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-teal-main">{log.laporan?.kode || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-xs font-semibold text-neutral-text-primary">{log.penerima?.name || '-'}</p>
                          <p className="text-[10px] text-neutral-text-secondary">
                            {log.tipe_penerima === 'atasan_unit' ? 'Atasan Unit' :
                              log.tipe_penerima === 'pic' ? 'PIC' : 'Pelapor'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${log.kanal === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                          {log.kanal === 'email' ? '📧 Email' : '🔔 Aplikasi'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${log.status === 'berhasil' ? 'bg-success/10 text-success' :
                          log.status === 'gagal' ? 'bg-danger/10 text-danger' :
                            'bg-neutral-bg text-neutral-text-secondary'
                          }`}>
                          {log.status === 'berhasil' ? '✓' : log.status === 'gagal' ? '✕' : '○'} {log.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-14 h-14 rounded-full bg-neutral-bg flex items-center justify-center mb-3">
                <Bell className="w-7 h-7 text-neutral-text-secondary opacity-40" />
              </div>
              <p className="text-sm text-neutral-text-secondary">Belum ada riwayat pengiriman pengingat.</p>
            </div>
          )}
        </Card>
      )}

      <Modal
        isOpen={showOverdueModal}
        onClose={() => {
          setShowOverdueModal(false);
          setOverdueModalPage(1);
          setOverdueModalSearch('');
          setOverdueModalSearchDraft('');
        }}
        title="Semua Laporan Melewati Batas Waktu"
        size="2xl"
        footer={(
          <Button variant="ghost" onClick={() => setShowOverdueModal(false)}>
            Tutup
          </Button>
        )}
      >
        <div className="space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-teal-dark">
                Daftar laporan terlambat untuk periode {selectedPeriodeLabel}
              </p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Gunakan daftar ini untuk memantau laporan yang sudah melewati target waktu dan perlu perhatian segera.
              </p>
            </div>
            <Badge className="self-start" size="sm">
              {overdueModalMeta.total} laporan
            </Badge>
          </div>

          <form
            className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto_auto] gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setOverdueModalPage(1);
              setOverdueModalSearch(overdueModalSearchDraft.trim());
            }}
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
              <input
                type="text"
                placeholder="Cari kode, judul, atau unit"
                value={overdueModalSearchDraft}
                onChange={(event) => setOverdueModalSearchDraft(event.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-light focus:border-teal-main transition-colors"
              />
            </div>
            <Button type="submit" variant="outline">
              Cari
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setOverdueModalPage(1);
                setOverdueModalSearchDraft('');
                setOverdueModalSearch('');
              }}
            >
              Reset
            </Button>
          </form>

          <div className="rounded-2xl border border-neutral-border overflow-hidden">
            <div className="px-4 py-3 bg-neutral-bg border-b border-neutral-border flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-neutral-text-primary">Daftar Laporan Terlambat</p>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Menampilkan laporan yang sudah lewat target penyelesaian sesuai filter yang Anda pilih.
                </p>
              </div>
              {overdueModalLoading && (
                <span className="text-xs text-neutral-text-secondary">Memuat data...</span>
              )}
            </div>

            <div className="max-h-[55vh] overflow-y-auto divide-y divide-neutral-border">
              {overdueModalLoading ? (
                <div className="py-12">
                  <Loading message="Memuat laporan terlambat..." />
                </div>
              ) : overdueModalData.length > 0 ? (
                overdueModalData.map((item, index) => {
                  const days = normalizeOverdueDays(item.hari_terlambat);
                  return (
                    <div key={item.laporan?.id || index} className="p-4 bg-white hover:bg-neutral-bg/60 transition-colors">
                      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                        <div className="flex items-start gap-4 min-w-0 flex-1">
                          <div className="shrink-0 w-14 h-14 rounded-2xl bg-danger/10 flex flex-col items-center justify-center border border-danger/10">
                            <span className="text-xl font-black text-danger leading-none">{days}</span>
                            <span className="text-[9px] font-bold uppercase tracking-wider text-danger">Hari</span>
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className="text-xs font-mono font-semibold text-teal-main">
                                {item.laporan?.kode}
                              </span>
                              {item.laporan?.status && (
                                <Badge variant="status" status={item.laporan.status} size="xs" useShortLabel />
                              )}
                              {item.laporan?.tingkat_risiko && (
                                <Badge variant="risk" risk={toRiskLevel(item.laporan.tingkat_risiko)} size="xs" />
                              )}
                            </div>
                            <p className="text-sm font-bold text-neutral-text-primary">
                              {item.laporan?.judul}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-text-secondary">
                              <span>Unit: {item.laporan?.unit?.name || '-'}</span>
                              <span>PIC: {item.pic || '-'}</span>
                              <span>Target: {item.target_efektif ? new Date(item.target_efektif).toLocaleDateString('id-ID') : '-'}</span>
                              <span>Sumber target: {item.sumber_target === 'keputusan_rapim' ? 'Keputusan RAPIM' : 'SLA awal'}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <Link to={`/laporan/${item.laporan?.id}`}>
                            <Button variant="outline" size="sm" icon={<Eye className="w-4 h-4" />}>
                              Lihat Detail
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-12 text-center">
                  <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-7 h-7 text-success" />
                  </div>
                  <p className="text-sm font-semibold text-success">Tidak ada laporan terlambat</p>
                  <p className="text-xs text-neutral-text-secondary mt-1">
                    Semua laporan masih berada dalam target waktu.
                  </p>
                </div>
              )}
            </div>
          </div>

          {overdueModalMeta.last_page > 1 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-text-secondary">
                Menampilkan <span className="font-semibold text-neutral-text-primary">
                  {overdueModalMeta.total === 0 ? 0 : ((overdueModalMeta.current_page - 1) * overdueModalMeta.per_page) + 1}
                  {' - '}
                  {Math.min(overdueModalMeta.current_page * overdueModalMeta.per_page, overdueModalMeta.total)}
                </span> dari <span className="font-semibold text-neutral-text-primary">{overdueModalMeta.total}</span> laporan
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={overdueModalMeta.current_page === 1}
                  onClick={() => setOverdueModalPage((page) => Math.max(1, page - 1))}
                >
                  Sebelumnya
                </Button>
                <span className="text-xs text-neutral-text-secondary">
                  Halaman {overdueModalMeta.current_page} / {overdueModalMeta.last_page}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={overdueModalMeta.current_page === overdueModalMeta.last_page}
                  onClick={() => setOverdueModalPage((page) => Math.min(overdueModalMeta.last_page, page + 1))}
                >
                  Berikutnya
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={showPpeppModal}
        onClose={() => {
          setShowPpeppModal(false);
          setPpeppModalPage(1);
          setPpeppModalSearch('');
          setPpeppModalSearchDraft('');
          setPpeppModalTab('semua');
        }}
        title="Semua Progress Tindak Lanjut (PPEPP)"
        size="2xl"
        footer={(
          <Button variant="ghost" onClick={() => setShowPpeppModal(false)}>
            Tutup
          </Button>
        )}
      >
        <div className="space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-teal-dark">
                Daftar tindak lanjut untuk periode {selectedPeriodeLabel}
              </p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Gunakan daftar ini untuk melihat seluruh progres tindak lanjut PIC, hambatan, dan status penyelesaian secara lebih lengkap.
              </p>
            </div>
            <Badge className="self-start" size="sm">
              {ppeppModalMeta.total} tindak lanjut
            </Badge>
          </div>

          <form
            className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto_auto] gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setPpeppModalPage(1);
              setPpeppModalSearch(ppeppModalSearchDraft.trim());
            }}
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
              <input
                type="text"
                placeholder="Cari kode atau judul laporan"
                value={ppeppModalSearchDraft}
                onChange={(event) => setPpeppModalSearchDraft(event.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-light focus:border-teal-main transition-colors"
              />
            </div>
            <Button type="submit" variant="outline">
              Cari
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setPpeppModalPage(1);
                setPpeppModalSearchDraft('');
                setPpeppModalSearch('');
                setPpeppModalTab('semua');
              }}
            >
              Reset
            </Button>
          </form>

          <div className="flex items-center gap-1 p-1 bg-neutral-bg/50 rounded-xl overflow-x-auto">
            {PPEPP_TABS.map((tab) => {
              const count = ppeppModalCounts[tab.id];
              const isActive = ppeppModalTab === tab.id;
              const IconComponent = tab.icon;

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setPpeppModalPage(1);
                    setPpeppModalTab(tab.id);
                  }}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap
                    ${isActive
                      ? 'bg-white text-teal-main shadow-sm'
                      : 'text-neutral-text-secondary hover:text-neutral-text-primary hover:bg-white/50'
                    }
                  `}
                >
                  {IconComponent && <IconComponent className="w-4 h-4" />}
                  <span>{tab.label}</span>
                  <span className={`
                    min-w-[22px] h-[22px] flex items-center justify-center rounded-full text-[11px] font-bold
                    ${isActive
                      ? 'bg-teal-main/10 text-teal-main'
                      : count > 0
                        ? 'bg-neutral-border/50 text-neutral-text-primary'
                        : 'bg-neutral-border/30 text-neutral-text-secondary'
                    }
                  `}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="rounded-2xl border border-neutral-border overflow-hidden">
            <div className="px-4 py-3 bg-neutral-bg border-b border-neutral-border flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-neutral-text-primary">Daftar Progress PPEPP</p>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Menyajikan tindak lanjut sesuai status dan kata kunci yang Anda pilih.
                </p>
              </div>
              {ppeppModalLoading && (
                <span className="text-xs text-neutral-text-secondary">Memuat data...</span>
              )}
            </div>

            <div className="max-h-[55vh] overflow-y-auto divide-y divide-neutral-border">
              {ppeppModalLoading ? (
                <div className="py-12">
                  <Loading message="Memuat tindak lanjut..." />
                </div>
              ) : ppeppModalData.length > 0 ? (
                ppeppModalData.map((item) => {
                  const daysRemaining = item.target_date
                    ? Math.ceil((new Date(item.target_date).getTime() - new Date().setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24))
                    : null;
                  const isOverdue = daysRemaining !== null && daysRemaining < 0;
                  const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 7;
                  const kondisi = (item as any).kondisi_selesai;
                  const isSelesai = kondisi === 'selesai_final';
                  const isTerhambat = kondisi === 'terhambat';
                  const isMenunggu = kondisi === 'menunggu_konfirmasi';
                  const displayState = getPpeppDisplayState(item, isOverdue);

                  const cardBg = isSelesai
                    ? 'bg-gradient-to-r from-success/5 to-transparent border-success/20 hover:border-success/40'
                    : isTerhambat
                      ? 'bg-gradient-to-r from-danger/5 to-transparent border-danger/20 hover:border-danger/40'
                      : isMenunggu
                        ? 'bg-gradient-to-r from-amber-50 to-transparent border-amber-200 hover:border-amber-300'
                        : isOverdue
                          ? 'bg-gradient-to-r from-danger/5 to-transparent border-danger/20 hover:border-danger/40'
                          : isUrgent
                            ? 'bg-gradient-to-r from-warning/5 to-transparent border-warning/20 hover:border-warning/40'
                            : 'bg-white border-neutral-border hover:border-teal-main/30';

                  return (
                    <div key={item.id} className={`p-4 border-b last:border-b-0 transition-colors ${cardBg}`}>
                      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">
                              {(item as any).laporan_masalah?.kode || (item as any).laporanMasalah?.kode}
                            </span>
                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${displayState.badgeClass}`}>
                              {displayState.label}
                            </span>
                            {daysRemaining !== null && !isSelesai && (
                              <span className={`text-[11px] font-semibold ${isOverdue ? 'text-danger' : isUrgent ? 'text-warning' : 'text-neutral-text-secondary'}`}>
                                {isOverdue ? `${Math.abs(daysRemaining)} hari terlambat` : `${daysRemaining} hari lagi`}
                              </span>
                            )}
                          </div>

                          <p className="text-sm font-bold text-neutral-text-primary">
                            {(item as any).laporan_masalah?.judul || (item as any).laporanMasalah?.judul}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-text-secondary">
                            <span>PIC: {item.pic_user?.name || item.pic_unit?.name || '-'}</span>
                            <span>Target: {item.target_date ? new Date(item.target_date).toLocaleDateString('id-ID') : '-'}</span>
                            <span>Progres: {item.progress_percentage || 0}%</span>
                          </div>

                          <div className="mt-3">
                            <ProgressBar
                              value={item.progress_percentage || 0}
                              color={displayState.progressColor}
                              size="sm"
                              showLabel={false}
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <Link to={`/tindak-lanjut/${item.id}`}>
                            <Button variant="outline" size="sm" icon={<Eye className="w-4 h-4" />}>
                              Lihat Detail
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-12 text-center">
                  <div className="w-14 h-14 rounded-full bg-neutral-bg flex items-center justify-center mx-auto mb-3">
                    <TrendingUp className="w-7 h-7 text-neutral-text-secondary opacity-40" />
                  </div>
                  <p className="text-sm font-semibold text-neutral-text-primary">Belum ada tindak lanjut yang sesuai</p>
                  <p className="text-xs text-neutral-text-secondary mt-1">
                    Coba ubah filter status atau kata kunci pencarian.
                  </p>
                </div>
              )}
            </div>
          </div>

          {ppeppModalMeta.last_page > 1 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-text-secondary">
                Menampilkan <span className="font-semibold text-neutral-text-primary">
                  {ppeppModalMeta.total === 0 ? 0 : ((ppeppModalMeta.current_page - 1) * ppeppModalMeta.per_page) + 1}
                  {' - '}
                  {Math.min(ppeppModalMeta.current_page * ppeppModalMeta.per_page, ppeppModalMeta.total)}
                </span> dari <span className="font-semibold text-neutral-text-primary">{ppeppModalMeta.total}</span> tindak lanjut
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={ppeppModalMeta.current_page === 1}
                  onClick={() => setPpeppModalPage((page) => Math.max(1, page - 1))}
                >
                  Sebelumnya
                </Button>
                <span className="text-xs text-neutral-text-secondary">
                  Halaman {ppeppModalMeta.current_page} / {ppeppModalMeta.last_page}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={ppeppModalMeta.current_page === ppeppModalMeta.last_page}
                  onClick={() => setPpeppModalPage((page) => Math.min(ppeppModalMeta.last_page, page + 1))}
                >
                  Berikutnya
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={showUnitModal}
        onClose={() => {
          setShowUnitModal(false);
          setSearchUnitQuery('');
        }}
        title={unitCopy.modalTitle}
        size="lg"
        footer={
          <div className="w-full flex justify-end">
            <Button variant="outline" onClick={() => setShowUnitModal(false)}>
              Tutup
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5">
            <p className="text-xs leading-relaxed text-blue-700">{unitCopy.helper}</p>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
            <input
              type="text"
              placeholder="Cari nama unit atau prodi..."
              value={searchUnitQuery}
              onChange={(e) => setSearchUnitQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-light focus:border-teal-main transition-colors"
            />
          </div>

          <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-2 scrollbar-thin">
            {filteredModalUnits.length > 0 ? (
              filteredModalUnits.map((unit, i) => {
                const rate = unit.completion_rate || 0;
                const colorClass = getRateBarClass(rate);
                const bgClass = rate >= 80 ? 'bg-success/5' : rate >= 50 ? 'bg-warning/5' : 'bg-danger/5';
                const borderClass = rate >= 80 ? 'border-success/20' : rate >= 50 ? 'border-warning/20' : 'border-danger/20';
                const secondaryCount = unitPerspective === 'pic'
                  ? (unit as UnitPicPerformance).terhambat
                  : (unit as UnitPelaporPerformance).overdue;

                return (
                  <div key={i} className={`p-4 rounded-xl border ${borderClass} hover:shadow-sm transition-all ${bgClass}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-white border border-neutral-border text-xs font-bold text-neutral-text-secondary shadow-sm">
                          {i + 1}
                        </span>
                        <span className="text-sm font-bold text-neutral-text-primary">{unit.unit}</span>
                      </div>
                      <span className={`text-xl font-black ${rate >= 80 ? 'text-success' : rate >= 50 ? 'text-warning' : 'text-danger'}`}>
                        {rate}%
                      </span>
                    </div>

                    <div className="w-full bg-white/60 rounded-full h-2.5 overflow-hidden mb-3 border border-neutral-border/30">
                      <div className={`h-full rounded-full transition-all duration-500 ${colorClass}`} style={{ width: `${rate}%` }} />
                    </div>

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-neutral-text-secondary">
                      <span className="flex items-center gap-1.5 font-medium">
                        <span className="w-2.5 h-2.5 rounded-full bg-success shadow-sm"></span>
                        <span className="text-success">{unit.selesai} Selesai</span>
                      </span>
                      {secondaryCount > 0 && (
                        <span className="flex items-center gap-1.5 font-bold">
                          <span className="w-2.5 h-2.5 rounded-full bg-danger animate-pulse shadow-sm"></span>
                          <span className="text-danger">
                            {secondaryCount} {unitPerspective === 'pic' ? 'Terhambat' : 'Overdue'}
                          </span>
                        </span>
                      )}
                      {unitPerspective === 'pic' && (
                        <span className="flex items-center gap-1.5 font-medium">
                          <span className="w-2.5 h-2.5 rounded-full bg-info shadow-sm"></span>
                          <span className="text-info">{(unit as UnitPicPerformance).avg_progress}% Rata-rata</span>
                        </span>
                      )}
                      <span className="ml-auto font-bold bg-white px-2.5 py-1 rounded-md border border-neutral-border/50">
                        Total: {unit.total}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12 bg-neutral-bg/30 rounded-xl border border-dashed border-neutral-border">
                <Users className="w-12 h-12 mx-auto text-neutral-text-secondary opacity-30 mb-3" />
                <p className="text-sm font-semibold text-neutral-text-primary">Unit tidak ditemukan</p>
                <p className="text-xs text-neutral-text-secondary mt-1">Coba gunakan kata kunci pencarian yang lain.</p>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MonitoringDashboard;
